import React, { useState, useEffect } from 'react';
import { Widget, Table, ColumnProps, MasterDetailForm, Row, Col, Button } from '@digital/ui';
import { Icon, Collapse, Modal } from 'antd';
import { mountFormFilterDropDown } from '@app/components/FormFilterDropDown';
import { ResumoControleNaoPrevisto, getResumoControleNaoPrevisto } from '@app/api/naoPrevistos/controle';
import { Excel } from 'antd-table-saveas-excel';

interface Props {
    ano: number;
}

const AbaResumo = ({ ano }: Props) => {
    const { Panel } = Collapse;

    const [dataSource, setDataSource] = useState<ResumoControleNaoPrevisto[]>();
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState<ResumoControleNaoPrevisto | null>(null);
    const [escopoModalVisible, setEscopoModalVisible] = useState(false);
    const [escopoTexto, setEscopoTexto] = useState<string>('');
    const [escopoTitulo, setEscopoTitulo] = useState<string>('');

    useEffect(() => {
        if (ano) {
            getResumoControleNaoPrevisto(ano).then(setDataSource);
        }
    }, [ano]);

    const openModal = (item: ResumoControleNaoPrevisto) => {
        setSelectedItem(item);
        setModalVisible(true);
    };

    const closeModal = () => {
        setSelectedItem(null);
        setModalVisible(false);
    };

    const openEscopoModal = (escopo: string, codigoSolicitacao: string) => {
        setEscopoTexto(escopo || '');
        setEscopoTitulo(`Escopo - ${codigoSolicitacao}`);
        setEscopoModalVisible(true);
    };

    const closeEscopoModal = () => {
        setEscopoTexto('');
        setEscopoTitulo('');
        setEscopoModalVisible(false);
    };

    // Trunca o texto para um máximo de caracteres
    const truncateText = (text: string, maxLength: number = 15): string => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Verifica se o texto precisa ser truncado
    const needsTruncation = (text: string): boolean => {
        if (!text) return false;
        // Se tiver mais de 15 caracteres ou quebra de linha, precisa truncar
        return text.length > 15 || text.includes('\n');
    };

    const columns: ColumnProps<ResumoControleNaoPrevisto>[] = [
        {
            title: 'Cód. Solicitação',
            dataIndex: 'codigoSolicitacaoTexto',
            align: 'left',
            ...mountFormFilterDropDown('codigoSolicitacaoTexto', 'Pesquisar'),
        },
        {
            title: 'Cód. Não Previsto',
            dataIndex: 'codigoNaoPrevistoTexto',
            align: 'left',
            ...mountFormFilterDropDown('codigoNaoPrevistoTexto', 'Pesquisar')
        },
        {
            title: 'Tipo',
            dataIndex: 'tipo',
            align: 'left',
            ...mountFormFilterDropDown('tipo', 'Pesquisar')
        },
        {
            title: 'Solicitante',
            dataIndex: 'nomeSolicitante',
            align: 'left',
            ...mountFormFilterDropDown('nomeSolicitante', 'Pesquisar')
        },
        {
            title: 'Responsável',
            dataIndex: 'nomeResponsavel',
            align: 'left',
            ...mountFormFilterDropDown('nomeResponsavel', 'Pesquisar')
        },
        {
            title: 'Diretoria',
            dataIndex: 'diretoriaSolicitante',
            align: 'left',
            ...mountFormFilterDropDown('diretoriaSolicitante', 'Pesquisar')
        },
        {
            title: 'Área',
            dataIndex: 'areaSolicitante',
            align: 'left',
            ...mountFormFilterDropDown('areaSolicitante', 'Pesquisar')
        },
        {
            title: 'Departamento',
            dataIndex: 'departamentoSolicitante',
            align: 'left',
            ...mountFormFilterDropDown('departamentoSolicitante', 'Pesquisar')
        },
        {
            title: 'Cód. do Projeto',
            dataIndex: 'codigoProjeto',
            align: 'left',
            ...mountFormFilterDropDown('codigoProjeto', 'Pesquisar')
        },
        {
            title: 'N° TOTVS',
            dataIndex: 'numeroTotvs',
            align: 'right',
            ...mountFormFilterDropDown('numeroTotvs', 'Pesquisar')
        },
        {
            title: 'Nome do Projeto',
            dataIndex: 'nomeProjeto',
            align: 'left',
            ...mountFormFilterDropDown('nomeProjeto', 'Pesquisar')
        },
        {
            title: 'Total R$',
            dataIndex: 'total',
            align: 'center',
            ...mountFormFilterDropDown('total', 'Pesquisar')
        },
        {
            title: 'Motivador',
            dataIndex: 'motivador',
            align: 'left',
            ...mountFormFilterDropDown('motivador', 'Pesquisar')
        },
        {
            title: 'Escopo',
            dataIndex: 'escopo',
            align: 'left',
            ...mountFormFilterDropDown('escopo', 'Pesquisar'),
            width: 200,
            render: (text: string, record: ResumoControleNaoPrevisto) => {
                if (!text) return <span>-</span>;
                
                const needsModal = needsTruncation(text);
                const displayText = needsModal ? truncateText(text, 15) : text;
                
                return (
                    <span
                        style={{
                            cursor: needsModal ? 'pointer' : 'default',
                            color: needsModal ? '#1890ff' : 'inherit',
                            textDecoration: needsModal ? 'underline' : 'none',
                            display: 'inline-block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle'
                        }}
                        onClick={needsModal ? () => openEscopoModal(text, record.codigoSolicitacaoTexto) : undefined}
                        title={needsModal ? "Clique para ver o texto completo" : text}
                    >
                        {displayText}
                    </span>
                );
            }
        },
        {
            title: 'Data do Último Status',
            dataIndex: 'dataUltimoStatus',
            align: 'center',
            ...mountFormFilterDropDown('dataUltimoStatus', 'Pesquisar')
        },
        {
            title: 'Status',
            dataIndex: 'status',
            align: 'left',
            ...mountFormFilterDropDown('status', 'Pesquisar')
        },
        {
            title: 'Emissão',
            dataIndex: 'dataAbertura',
            align: 'center',
            ...mountFormFilterDropDown('dataAbertura', 'Pesquisar')
        },

        /* ----------------------------------- */
        /*  ADICIONA AQUI A COLUNA DE AÇÕES    */
        /* ----------------------------------- */
        {
            title: 'Ações',
            align: 'center',
            render: (_, record) => (
                <Button size="small" type="link" onClick={() => openModal(record)}>
                    Ver Resumo
                </Button>
            )
        }
    ];

    const columnsToExport = [
        { title: 'Cód. Solicitação', dataIndex: 'codigoSolicitacaoTexto' },
        { title: 'Cód. Não Previsto', dataIndex: 'codigoNaoPrevistoTexto' },
        { title: 'Tipo', dataIndex: 'tipo' },
        { title: 'Solicitante', dataIndex: 'nomeSolicitante' },
        { title: 'Responsável', dataIndex: 'nomeResponsavel' },
        { title: 'Diretoria', dataIndex: 'diretoriaSolicitante' },
        { title: 'Área', dataIndex: 'areaSolicitante' },
        { title: 'Departamento', dataIndex: 'departamentoSolicitante' },
        { title: 'Cód. do Projeto', dataIndex: 'codigoProjeto' },
        { title: 'N° TOTVS', dataIndex: 'numeroTotvs' },
        { title: 'Nome do Projeto', dataIndex: 'nomeProjeto' },
        { title: 'Total R$', dataIndex: 'total' },
        { title: 'Motivador', dataIndex: 'motivador' },
        { title: 'Escopo', dataIndex: 'escopo' },
        { title: 'Data do Último Status', dataIndex: 'dataUltimoStatus' },
        { title: 'Status', dataIndex: 'status' },
        { title: 'Emissão', dataIndex: 'dataAbertura' },
        { title: 'Aprovação Final', dataIndex: 'dataAprovacaoFinal' },
        { title: 'Qtd dias aprovação', dataIndex: 'contagemDiasAprovacaoFinal' },
    ];

    const exportToExcel = () => {
        if (!dataSource) return;

        const excel = new Excel();
        excel
            .setTHeadStyle({
                background: 'FFfdfdfd',
                fontName: 'robotoRegular',
            })
            .setTBodyStyle({ border: true })
            .addSheet('Controle')
            .addColumns(columnsToExport as any)
            .addDataSource(dataSource)
            .saveAs('Controle.xlsx');
    };

    /* ---------------------------- */
    /*   RENDER DO MODAL            */
    /* ---------------------------- */
    const renderModal = () => (
        <Modal
            visible={modalVisible}
            title={`Resumo - ${selectedItem?.codigoSolicitacaoTexto}`}
            onCancel={closeModal}
            footer={[<Button key="close" onClick={closeModal}>Fechar</Button>]}
            width={800}
        >
            {selectedItem && (
                <div style={{ lineHeight: 1.7 }}>
                    <p><b>Solicitação:</b> {selectedItem.codigoSolicitacaoTexto}</p>
                    <p><b>Não Previsto:</b> {selectedItem.codigoNaoPrevistoTexto}</p>
                    <p><b>Tipo:</b> {selectedItem.tipo}</p>
                    <p><b>Solicitante:</b> {selectedItem.nomeSolicitante}</p>
                    <p><b>Responsável:</b> {selectedItem.nomeResponsavel}</p>
                    <p><b>Diretoria:</b> {selectedItem.diretoriaSolicitante}</p>
                    <p><b>Área:</b> {selectedItem.areaSolicitante}</p>
                    <p><b>Departamento:</b> {selectedItem.departamentoSolicitante}</p>

                    <hr />

                    <p><b>Projeto:</b> {selectedItem.nomeProjeto}</p>
                    <p><b>Total:</b> R$ {selectedItem.total}</p>
                    <p><b>Motivador:</b> {selectedItem.motivador}</p>
                    <p><b>Escopo:</b> {selectedItem.escopo}</p>

                    <hr />

                    <p><b>Status Atual:</b> {selectedItem.status}</p>
                    <p><b>Último Status:</b> {selectedItem.dataUltimoStatus}</p>
                    <p><b>Emissão:</b> {selectedItem.dataAbertura}</p>

                    <hr />

                    <p><b>Aprovação Final:</b> {selectedItem.dataAprovacaoFinal}</p>
                    <p><b>Qtd dias aprovação:</b> {selectedItem.contagemDiasAprovacaoFinal}</p>
                </div>
            )}
        </Modal>
    );

    const renderEscopoModal = () => (
        <Modal
            visible={escopoModalVisible}
            title={escopoTitulo}
            onCancel={closeEscopoModal}
            footer={[<Button key="close" onClick={closeEscopoModal}>Fechar</Button>]}
            width={800}
        >
            <div style={{ 
                lineHeight: 1.7, 
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '60vh',
                overflowY: 'auto'
            }}>
                {escopoTexto || 'Sem escopo informado.'}
            </div>
        </Modal>
    );

    return (
        <>
            {renderModal()}
            {renderEscopoModal()}

            <Collapse bordered={false} defaultActiveKey={['1']} expandIcon={() => <Icon type='arrows-right_4' />}>
                <Panel key="1" header="Resumo" style={{ border: 0 }}>
                    <MasterDetailForm>
                        <Widget
                            actions={[
                                <Button
                                    key="export"
                                    type="primary"
                                    size="small"
                                    icon="files-ms_excel"
                                    onClick={exportToExcel}
                                >
                                    Exportar Resumo Não Previsto
                                </Button>
                            ]}
                        >
                            <Row>
                                <Col span={12}>
                                    <Table
                                        rowKey="codigoSolicitacaoTexto"
                                        columns={columns}
                                        dataSource={dataSource}
                                        pagination={{ pageSize: 10 }}
                                    />
                                </Col>
                            </Row>
                        </Widget>
                    </MasterDetailForm>
                </Panel>
            </Collapse>
        </>
    );
};

export default AbaResumo;
